import { faEnvelope, faLock, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { NavLink } from 'react-router-dom'
import { useModal } from '~/hooks/useModal'

/**
 * Shown in place of the deposit/withdraw modal when the banking surface is closed to this user.
 * The balance button used to just swallow the click, which reads as a broken button rather than
 * a closed door — this says which door and, where there is one, how to open it.
 */
export const BankUnavailable = ({ reason }: { reason: 'email' | 'disabled' | 'frozen' }) => {
    const { unSetModal } = useModal()

    /*
      `frozen` is its own reason rather than reusing `disabled`, because the two say opposite
      things and only one of them would be true. The disabled copy tells the player the platform
      is paused and to check back shortly -- read by somebody whose own account is held, that is
      false on every count: nothing is paused, waiting will not help, and the one thing they can
      usefully do is contact support. It says nothing about where the money is, because a hold
      is not a loss and the player should not be led to believe it is.
    */
    const content =
        reason === 'email'
            ? {
                  icon: faEnvelope,
                  title: 'Confirm your email first',
                  body: 'Deposits and withdrawals unlock once your email address is confirmed. It takes a minute.',
                  action: { label: 'Confirm email', to: '/activate' },
              }
            : reason === 'frozen'
              ? {
                    icon: faTriangleExclamation,
                    title: 'Your funds are on hold',
                    body: 'Withdrawals and purchases are unavailable on this account while a hold is in place. Contact support if you believe this is a mistake.',
                    action: undefined,
                }
              : {
                    icon: faLock,
                    title: 'Banking is temporarily closed',
                    body: 'Deposits and withdrawals are paused right now. Your balance is safe and nothing has been lost. Check back shortly.',
                    action: undefined,
                }

    return (
        <div className='bg-black border border-white/20 p-6 md:p-8 w-full max-w-xl flex flex-col items-center gap-4 text-center'>
            <div className='h-14 w-14 flex items-center justify-center border border-white/20 rounded-full'>
                <FontAwesomeIcon icon={content.icon} className='text-xl text-white/70' />
            </div>
            <h2 className='uppercase text-lg md:text-xl font-bold tracking-wide'>{content.title}</h2>
            <p className='text-sm text-white/60 max-w-sm'>{content.body}</p>
            {content.action && (
                <NavLink
                    to={content.action.to}
                    onClick={unSetModal}
                    className='bg-br1-gradient font-bold uppercase tracking-wide px-6 py-3 min-h-[44px] flex items-center hover:outline'
                >
                    {content.action.label}
                </NavLink>
            )}
        </div>
    )
}
